import React, { useEffect, useState, useMemo, useContext } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  Button,
  Input,
  Space,
  Breadcrumb,
  Typography,
  Card,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { list_customers } from "../../../api/CustomerApi";
import { Button as CustomButton } from "../../../components/ui/button";
import { Download } from "lucide-react";
import { get_customers_to_download_chunked } from "../../../api/AdminApi";
import { GlobalContext } from "../../../App";
import { format } from "date-fns";
const { Title } = Typography;

const CustomerPage = () => {
  const navigate = useNavigate();
  const { setToastOpen, setToastMessage, setToastSeverity } =
    useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const getData = async () => {
    try {
      setLoading(true);
      const response = await list_customers(
        pagination.current,
        pagination.pageSize,
        debouncedSearchText
      );

      // Ensure response is an array
      const customersData = Array.isArray(response.data.items)
        ? response.data.items
        : [];

      setAllCustomers(customersData);
      setCustomers(customersData);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta.total,
      }));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setAllCustomers([]);
      setCustomers([]);
      setLoading(false);
    }
  };
  const onSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (!value.trim()) {
      setCustomers(allCustomers);
      return;
    }
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(newPagination);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      width: "50%",
      sorter: (a, b) => a.customer_name?.localeCompare(b.customer_name || ""),
    },

    {
      title: "Access Code",
      dataIndex: "access_code",
      key: "access_code",
      width: "20%",
      sorter: (a, b) => a.access_code?.localeCompare(b.access_code || ""),
    },
    {
      title: "Default Voice Rate",
      dataIndex: "default_voice_rate",
      key: "default_voice_rate",
      width: "20%",
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Button type="link" onClick={() => handleEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  const handleAddCustomer = () => {
    navigate("/admin/customers/add");
  };

  const handleEdit = (record) => {
    navigate(`/admin/customers/edit/${record.customer_id}`);
  };
  const downloadCSV = async () => {
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      let response = await get_customers_to_download_chunked((count) => {
        setDownloadProgress(count);
      });

      const data = response.data || [];

      if (!data.length) {
        setToastMessage("No data available to download.");
        setToastOpen(true);
        setToastSeverity("warning");
        return;
      }

      // Dynamically get columns from response keys
      const dynamicColumns = Object.keys(data[0]);

      // Create CSV header row
      const header = dynamicColumns.map((key) => `"${key}"`).join(",");

      // Format CSV rows
      const csvRows = data.map((row) => {
        return dynamicColumns
          .map((key) => {
            const value = row[key];
            return `"${value ? String(value).replace(/"/g, '""') : ""}"`;
          })
          .join(",");
      });

      // Combine header and rows
      const csvContent = [header, ...csvRows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `customer_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      setToastMessage(error);
      setToastOpen(true);
      setToastSeverity("error");
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };
  const handleDownloadProgress = (count) => {
    setDownloadProgress(count);
  };
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 700);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    getData();
  }, [pagination.current, pagination.pageSize, debouncedSearchText]);

  return (
    <div className="p-0">
      <Breadcrumb className="mb-6">
        <Breadcrumb.Item>Admin</Breadcrumb.Item>
        <Breadcrumb.Item>Customers</Breadcrumb.Item>
      </Breadcrumb>

      <div className="flex justify-between items-center mb-6">
        <Title level={3} className="mb-0">
          Customers
        </Title>
        {/* <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddCustomer}
        >
          Add Customer
        </Button> */}
        <div className="flex gap-2">
          <CustomButton
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={downloadCSV}
            disabled={isDownloading}
          >
            <Download className="h-4 w-4" />
            {isDownloading
              ? downloadProgress > 0
                ? `Downloading... (${downloadProgress} records)`
                : "Preparing download..."
              : "Download CSV"}
          </CustomButton>

          <CustomButton
            variant="outline"
            className="flex items-center gap-2 cursor-pointer border-blue-100 text-blue-600 hover:bg-blue-50"
            onClick={() => navigate("/admin/customers/add")}
          >
            <PlusOutlined className={`h-4 w-4`} />
            Add Customer
          </CustomButton>
        </div>
      </div>

      <Card className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="w-1/3">
            <Input
              placeholder="Search customers..."
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e.target.value)}
              allowClear
            />
          </div>
          <div>
            <span style={{ marginLeft: 8 }}>
              {selectedRowKeys.length > 0
                ? `Selected ${selectedRowKeys.length} items`
                : ""}
            </span>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={customers}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${total} records`,
            pageSizeOptions: ["10", "20", "30", "50", "100"],
            size: "default",
            position: ["bottomRight"],
            defaultPageSize: 30,
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize: pageSize,
              }));
            },
            onShowSizeChange: (current, size) => {
              setPagination((prev) => ({
                ...prev,
                current: 1, // Reset to first page when changing page size
                pageSize: size,
              }));
            },
          }}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          rowKey="customer_id"
          size="small"
        />
      </Card>
    </div>
  );
};

export default CustomerPage;
